"use client";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import * as React from "react";
import { cva } from "../../lib/cva";
import { cn } from "../../lib/utils";
import type { SpacingToken } from "./box-classes";
import type { Omittable } from "../../lib/props";
import { useKeyboardAnchorFollow } from "../../hooks/use-keyboard-anchor-follow";
import { useTouchAwareInitialFocus } from "../../hooks/use-open-focus";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";
import { useIsMobile } from "../../hooks/use-media-query";
import {
  Drawer,
  DrawerClose,
  DrawerInsetPanel,
  DrawerPopup,
  DrawerTrigger,
  type DrawerSize,
} from "./drawer";

// A responsive Popover presents as a bottom sheet on a phone (the library
// Drawer); every part reads this to pick its drawer twin.
const PopoverSheetContext = React.createContext(false);

/** `true` inside a `Popover responsive` that is presenting as a phone sheet —
 *  for content that shapes itself differently in a sheet than in a popover
 *  (a fixed frame width, a list's own height cap). */
export function usePopoverSheet(): boolean {
  return React.useContext(PopoverSheetContext);
}

export const popoverPopupVariants = cva(
  "relative size-full max-h-(--available-height) overflow-clip px-(--viewport-inline-padding) has-data-[slot=calendar]:p-2 data-instant:transition-none **:data-current:data-ending-style:opacity-0 **:data-current:data-starting-style:opacity-0 **:data-previous:data-ending-style:opacity-0 **:data-previous:data-starting-style:opacity-0 **:data-current:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-previous:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-current:opacity-100 **:data-previous:opacity-100 **:data-current:transition-opacity **:data-previous:transition-opacity",
  {
    defaultVariants: {
      padding: "md",
    },
    variants: {
      /**
       * Viewport inset, both axes. The horizontal half travels as a custom
       * property because content that must bleed to the popup's edge — a
       * full-width action bar — cancels it with a negative margin and needs
       * the value, not the class.
       *
       * Declaring the property here rather than on the Popup is deliberate: a
       * custom property set on an ancestor loses to one declared on the
       * element itself, so a caller reaching in to override
       * `--viewport-inline-padding` from the Popup has no effect at all. That
       * is what this prop replaces.
       */
      padding: {
        none: "py-0 [--viewport-inline-padding:--spacing(0)]",
        "2xs": "py-0.5 [--viewport-inline-padding:--spacing(0.5)]",
        xs: "py-1 [--viewport-inline-padding:--spacing(1)]",
        sm: "py-2 [--viewport-inline-padding:--spacing(2)]",
        base: "py-3 [--viewport-inline-padding:--spacing(3)]",
        md: "py-4 [--viewport-inline-padding:--spacing(4)]",
        lg: "py-6 [--viewport-inline-padding:--spacing(6)]",
        xl: "py-8 [--viewport-inline-padding:--spacing(8)]",
        "2xl": "py-12 [--viewport-inline-padding:--spacing(12)]",
      } satisfies Record<SpacingToken, string>,
    },
  },
);

/**
 * Fixed popup width, by what the popup HOLDS. A popover whose content cannot
 * size itself has to state a width somewhere; these are the two shapes that
 * recur:
 *
 * - `picker` — a searchable list of one kind of thing (an entity selector,
 *   an address lookup). THE list rung: the same picker is the same object
 *   wherever it opens, so `EntitySelectorContent` takes it by default and a
 *   menu's `panel` submenu matches it.
 * - `card` — one column read in place: a small form, a history list, a hover
 *   preview card. Wide enough for a label/value row, narrow enough to stay a
 *   card rather than a panel.
 * - `builder` — rows that hold several controls side by side (a filter or
 *   query builder), which the `card` rung crams.
 * - `panel` — a two-track tile grid or a field editor with rows of its own
 *   (a searchable type catalog, a rich value editor): a small pane opened in
 *   place, the widest a popover gets before it is a dialog.
 *
 * Omit for a content-sized popup, which is most of them. A raw `w-[…]` at a
 * call site is how one genre ends up holding 280, 300, 320 and 360 at once.
 *
 * Pixels through `style` rather than a Tailwind class: the popup's base width
 * is `w-(--popup-width,auto)`, which Base UI drives for its open/resize
 * transition, so an inline width states the intent without racing it.
 *
 * Every rung is a ceiling, not a promise: the popup also caps itself at the
 * positioner's `--available-width`, so on a phone a `panel` popup is as wide
 * as the viewport lets it be, never wider.
 */
export type PopoverPopupWidth = "picker" | "card" | "builder" | "panel";

export const POPOVER_POPUP_WIDTH: Record<PopoverPopupWidth, number> = {
  picker: 280,
  card: 300,
  builder: 360,
  panel: 420,
};

/** What a popup may hang off instead of a trigger of its own: an element, a
 *  ref to one, a resolver, or a virtual element (an object carrying
 *  `getBoundingClientRect`). Prefer a virtual element for an anchor the popup
 *  outlives — its rect is read at position time, so it cannot go stale. */
export type PopoverAnchor = PopoverPrimitive.Positioner.Props["anchor"];

/**
 * The popover root. On a coarse pointer it opens MODAL unless the caller
 * decides otherwise: Base UI then mounts a transparent backdrop under the
 * popup, so the tap that closes it is spent on the backdrop and never lands
 * on whatever sat beneath the finger — a menu, a select and a dialog already
 * carry that backdrop; a non-modal popover let the tap through, closing the
 * popup AND pressing the row, chip or tile under it. A mouse keeps the
 * non-modal default: a desktop click-away that also lands is the grammar
 * the toolbar chips rely on (press another chip while one is open), and a
 * mouse can aim past the popup where a finger cannot.
 *
 * Modal here means only the backdrop and, for a popup opened by touch, no
 * scroll lock unless the popup is viewport-wide (Base UI's own rule, so a
 * swipe outside still dismisses). Focus is trapped only if a `PopoverClose`
 * is rendered inside, Base UI's condition, which touch screen readers need
 * to escape a modal popup anyway.
 */
export type PopoverProps<Payload = unknown> = PopoverPrimitive.Root.Props<Payload> &
  Omittable<{
    /** Present as a bottom sheet on a phone. For a popover that holds a
     *  searchable list — a command list, a catalog — whose search field
     *  takes the keyboard: inside a phone sheet or dialog a popover has
     *  nowhere to go when the keyboard rises, while a sheet's search lifts it
     *  to the top under the keys. Default false: a popover holding one value
     *  editor or a preview card stays a popover everywhere. */
    responsive?: boolean;
    /** Sheet only: the drawer's height — see `DrawerSize`. Default `half`. */
    sheetSize?: DrawerSize;
  }>;

export function Popover<Payload = unknown>({
  modal,
  responsive = false,
  sheetSize = "half",
  ...props
}: PopoverProps<Payload>): React.ReactElement {
  const touch = useIsTouchDevice();
  const isMobile = useIsMobile();
  const sheet = responsive && isMobile;
  return (
    <PopoverSheetContext.Provider value={sheet}>
      {sheet ? (
        <Drawer {...(props as React.ComponentProps<typeof Drawer>)} size={sheetSize} />
      ) : (
        <PopoverPrimitive.Root modal={modal ?? touch} {...props} />
      )}
    </PopoverSheetContext.Provider>
  );
}

export function PopoverTrigger({
  className,
  children,
  ...props
}: PopoverPrimitive.Trigger.Props): React.ReactElement {
  if (React.useContext(PopoverSheetContext)) {
    return (
      <DrawerTrigger
        className={className}
        data-slot="popover-trigger"
        {...(props as React.ComponentProps<typeof DrawerTrigger>)}
      >
        {children}
      </DrawerTrigger>
    );
  }
  return (
    <PopoverPrimitive.Trigger
      className={className}
      data-slot="popover-trigger"
      {...props}
    >
      {children}
    </PopoverPrimitive.Trigger>
  );
}

export function PopoverPopup({
  children,
  className,
  side = "bottom",
  align = "center",
  sideOffset = 4,
  alignOffset = 0,
  collisionPadding,
  collisionAvoidance,
  tooltipStyle = false,
  padding,
  anchor,
  portalProps,
  width,
  style,
  initialFocus,
  sheetTitle,
  sheetScrollable,
  ref,
  ...props
}: PopoverPrimitive.Popup.Props & Omittable<{
  /**
   * Viewport inset. `none` for a list-shaped popup whose rows run edge to edge
   * and own their own padding — a menu, a selector pool, a preview card.
   */
  padding?: SpacingToken;
  portalProps?: PopoverPrimitive.Portal.Props;
  side?: PopoverPrimitive.Positioner.Props["side"];
  align?: PopoverPrimitive.Positioner.Props["align"];
  sideOffset?: PopoverPrimitive.Positioner.Props["sideOffset"];
  alignOffset?: PopoverPrimitive.Positioner.Props["alignOffset"];
  /** Keep-out inset for collision avoidance, e.g. `{ top: railTop }` so a
   * popup anchored to a tall column never floats above that column's edge. */
  collisionPadding?: PopoverPrimitive.Positioner.Props["collisionPadding"];
  /** How the positioner dodges the boundary. Omit for Base UI's default
   *  (flip the side, shift the alignment). */
  collisionAvoidance?: PopoverPrimitive.Positioner.Props["collisionAvoidance"];
  tooltipStyle?: boolean;
  anchor?: PopoverAnchor;
  /** Fixed width by popup kind — see PopoverPopupWidth. */
  width?: PopoverPopupWidth;
  /** Plain style object only — the width rung merges into it, and a caller's
   *  own `width` here still wins. */
  style?: React.CSSProperties;
  /** Sheet only: a small centred caption under the grab bar naming what the
   *  sheet lists ("Assignees") — for a `responsive` popover whose content
   *  is a searchable list with no header of its own. The popover shows
   *  nothing: its trigger is right there. See `DrawerInsetPanel`. */
  sheetTitle?: React.ReactNode;
  /** Sheet only: `false` for a body that must not scroll (a palette sized
   *  to the sheet). See `DrawerInsetPanel scrollable`. */
  sheetScrollable?: boolean;
}>): React.ReactElement {
  // Focus on open reads the pointer, not just the click (WebKit stamps a
  // finger's click "mouse"): a coarse-pointer open lands on the popup, never
  // on a field inside it, unless that field claimed focus itself.
  const { ref: focusRef, initialFocus: touchInitialFocus } =
    useTouchAwareInitialFocus<HTMLDivElement>(ref);
  const [popupEl, setPopupEl] = React.useState<HTMLDivElement | null>(null);
  const popupRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      focusRef(node);
      setPopupEl(node);
    },
    [focusRef],
  );
  // A field inside that does take the keyboard holds the popup's side and
  // scrolls its anchor clear of the keyboard — see the hook.
  const follow = useKeyboardAnchorFollow({ anchor, popup: popupEl });

  // The sheet: the Drawer's own popup, its panel the one scroller with the
  // sheet's inset. Everything that describes the popover's shape — side,
  // align, width, className, style, padding — is left to the popover; a
  // sheet is as wide as the phone. A field tapped inside lifts a half sheet
  // to the top stop, the drawer's own rule.
  if (React.useContext(PopoverSheetContext)) {
    return (
      <DrawerPopup
        initialFocus={initialFocus}
        ref={ref}
        showBar
        {...(props as React.ComponentProps<typeof DrawerPopup>)}
      >
        <DrawerInsetPanel
          data-slot="popover-sheet-panel"
          scrollable={sheetScrollable}
          title={sheetTitle}
        >
          {children}
        </DrawerInsetPanel>
      </DrawerPopup>
    );
  }

  return (
    <PopoverPrimitive.Portal {...portalProps}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        collisionAvoidance={follow.collisionAvoidance ?? collisionAvoidance}
        collisionPadding={collisionPadding}
        className={cn(
          "z-50 h-(--positioner-height) w-(--positioner-width) max-w-(--available-width)",
          // Position changes glide, so a popup follows a trigger that moves.
          // Base UI suppresses the glide through `data-instant`, which it only
          // ever sets when the ACTIVE TRIGGER changes — an externally anchored
          // popup has no trigger, so the flag can never arrive and every
          // reposition it makes (the second pass floating-ui runs once the
          // anchor is registered, an anchor-tracking tick, a collision reflow)
          // becomes a visible 150ms slide from wherever the popup last sat.
          // Those popups snap instead.
          anchor == null &&
            "transition-[top,left,right,bottom,transform] data-instant:transition-none",
        )}
        data-slot="popover-positioner"
        side={follow.side ?? side}
        sideOffset={sideOffset}
      >
        <PopoverPrimitive.Popup
          // The width ceiling. The Positioner caps ITSELF at the space
          // collision avoidance leaves, but a popup with a stated width — the
          // `width` rung or a caller's class — would still overflow it and run
          // off a phone's edge, so the popup reads the same `--available-width`
          // (set on the Positioner; it inherits). The `100vw` half is the
          // FIRST paint, before the variable exists: an app whose root clips
          // horizontal overflow makes that root the collision boundary, and
          // floating-ui measures it by scroll size — which an uncapped 420px
          // popup has just widened. The boundary then reports the space the
          // popup itself took, and the popup settles hanging past the screen.
          // Never wider than the viewport on any paint, and the document never
          // grows.
          className={cn(
            "relative flex h-(--popup-height,auto) w-(--popup-width,auto) max-w-[min(var(--available-width,100vw),100vw)] origin-(--transform-origin) rounded-lg border bg-popover not-dark:bg-clip-padding text-popover-foreground shadow-popover outline-none transition-[width,height,scale,opacity] before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] has-data-[slot=calendar]:rounded-xl has-data-[slot=calendar]:before:rounded-[calc(var(--radius-xl)-1px)] data-starting-style:scale-98 data-starting-style:opacity-0 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
            tooltipStyle &&
              "w-fit text-balance rounded-md text-xs shadow-card before:rounded-[calc(var(--radius-md)-1px)]",
            className,
          )}
          // Portalled to the body, the popup inherits no surface scope, so it
          // names its own: descendant `border-border` draws the popover-tuned
          // hairline instead of the page's, which in dark sits under 3L off
          // the popover fill and vanishes.
          data-on="popover"
          data-slot="popover-popup"
          initialFocus={initialFocus ?? touchInitialFocus}
          ref={popupRef}
          style={width ? { width: POPOVER_POPUP_WIDTH[width], ...style } : style}
          {...props}
        >
          <PopoverPrimitive.Viewport
            className={cn(
              popoverPopupVariants({ padding }),
              // The padding prop wins. Absent one, a tooltip-style popup takes
              // a tighter inset than the ramp can express — 4px block, 8px
              // inline — out-merging the composer's default.
              padding == null &&
                tooltipStyle &&
                "py-1 [--viewport-inline-padding:--spacing(2)]",
              !tooltipStyle && "not-data-transitioning:overflow-y-auto",
            )}
            data-slot="popover-viewport"
          >
            {children}
          </PopoverPrimitive.Viewport>
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

export function PopoverClose({
  ...props
}: PopoverPrimitive.Close.Props): React.ReactElement {
  if (React.useContext(PopoverSheetContext)) {
    return (
      <DrawerClose
        data-slot="popover-close"
        {...(props as React.ComponentProps<typeof DrawerClose>)}
      />
    );
  }
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

export function PopoverTitle({
  className,
  ...props
}: PopoverPrimitive.Title.Props): React.ReactElement {
  return (
    <PopoverPrimitive.Title
      className={cn("font-semibold text-lg leading-none", className)}
      data-slot="popover-title"
      {...props}
    />
  );
}

export function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props): React.ReactElement {
  return (
    <PopoverPrimitive.Description
      className={cn("text-muted-foreground text-sm", className)}
      data-slot="popover-description"
      {...props}
    />
  );
}
